import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ThirdPartyStatus } from '@risksmart-app/domain/src/types/consts/third-party-status';
import type { ThirdPartyType } from '@risksmart-app/domain/src/types/consts/third-party-type';
import type {
  UpdateThirdPartyInput,
  UpdateThirdPartyMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import {
  toCriticality,
  toThirdPartyStatus,
  toThirdPartyType,
} from './thirdPartyEnums';

/**
 * Public input for the hook. Matches the third-party form call site where
 * Type/Status are plain strings (the select options ARE the enum values) and
 * Criticality is a coerced number. Everything else mirrors the GraphQL
 * UpdateThirdPartyInput (required id arrays + Id).
 */
type UpdateThirdPartyInputShape = UpdateThirdPartyInput & {
  /**
   * Optimistic-concurrency token (COI-622). Forwarded on the tRPC leg only —
   * the legacy `updateThirdPartyApi` input has no such field, so
   * `graphqlVariables` strips it before the Apollo call.
   */
  OriginalTimestamp?: string | null;
};

/**
 * tRPC input type derived from GraphQL UpdateThirdPartyInput.
 * Adds Id. Title/Type/Status are required by the tRPC router (Type/Status are
 * required native enums). `Criticality` is OPTIONAL — the column is a NULLABLE
 * integer with a CHECK 1..4, and the form offers a blank "-" option. Viewer
 * arrays are nullish for the tri-state (undefined = no change, [] = clear-all).
 */
export type UpdateThirdPartyTRPCInput = {
  Id: string;
  /**
   * Optimistic-concurrency token (COI-622) — the third-party row's last-seen
   * `ModifiedAtTimestamp`. Only this path locks on it.
   */
  OriginalTimestamp?: string | null;
  Title: string;
  Type: ThirdPartyType;
  Status: ThirdPartyStatus;
  Criticality?: number | null;
  Description?: string | null;
  CompanyName?: string | null;
  CompaniesHouseNumber?: string | null;
  Address?: string | null;
  CityTown?: string | null;
  Postcode?: string | null;
  Country?: string | null;
  PrimaryContactName?: string | null;
  ContactName?: string | null;
  ContactEmail?: string | null;
  CompanyDomain?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
  OwnerUserIds?: string[];
  OwnerGroupIds?: string[];
  ContributorUserIds?: string[];
  ContributorGroupIds?: string[];
  TagTypeIds?: string[];
  DepartmentTypeIds?: string[];
  ViewerUserIds?: string[] | null;
  ViewerGroupIds?: string[] | null;
};

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateThirdPartyMutation => {
  return {
    updateThirdPartyApi: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateThirdParty = createMutationHook({
  name: 'updateThirdParty',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.thirdParty.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateThirdPartyInputShape
  ): UpdateThirdPartyTRPCInput => ({
    ...variables,
    Type: toThirdPartyType(variables.Type),
    Status: toThirdPartyStatus(variables.Status),
    Criticality: toCriticality(variables.Criticality),
  }),
  invalidate: (trpc) => [
    trpc.frontend.thirdParty.register.queryKey(),
    trpc.frontend.thirdParty.getById.queryKey(),
    // COI-889: this hook rewrites the owner/contributor sets, whose triggers
    // maintain the grant cache `perm_v2_entity_permissions` reads (60-min
    // window, `refetchOnMount: false`, so it never self-heals).
    trpc.frontend.permission.getEntityPermissions.queryKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateThirdPartyMutation =>
    mapTrpcResponseToGraphQL(result),
  graphqlDocument: UpdateThirdPartyDocument,
  // COI-622: the legacy `updateThirdPartyApi` input has no OriginalTimestamp
  // field, so strip the concurrency token before the Apollo call — only the
  // tRPC leg locks on it.
  graphqlVariables: ({
    OriginalTimestamp: _token,
    ...variables
  }: UpdateThirdPartyInputShape) => ({
    object: variables,
  }),
  evictFields: ['third_party', 'third_party_by_pk'],
});
